import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { Coupon } from '../Firebase/firebaseApi.ts';

interface CouponSelectionProps {
  availableCoupons: Coupon[];
  selectedCoupon: Coupon | null;
  setSelectedCoupon: (coupon: Coupon | null) => void;
  couponProof: File | null;
  setCouponProof: (file: File | null) => void;
  showCouponCode: boolean;
  setShowCouponCode: (show: boolean) => void;
}

const CouponSelection: React.FC<CouponSelectionProps> = ({
  availableCoupons,
  selectedCoupon,
  setSelectedCoupon,
  couponProof,
  setCouponProof,
  showCouponCode,
  setShowCouponCode,
}) => {

  const handleCouponProofChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCouponProof(file);
      setShowCouponCode(true);
    }
  };
  
  const handleCouponSelect = (coupon: Coupon, isChecked: boolean) => {
    if (isChecked) {
      setSelectedCoupon(coupon);
      setCouponProof(null);
      // If proof is not needed, show coupon code immediately
      if (!coupon.needProof) {
        setShowCouponCode(true);
      } else {
        setShowCouponCode(false);
      }
    } else {
      setSelectedCoupon(null);
      setCouponProof(null);
      setShowCouponCode(false);
    }
  };

  return (
    <>
      {availableCoupons.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            קופונים זמינים:
          </Typography>
          {availableCoupons.map((coupon) => (
            <Box key={coupon.code} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCoupon?.code === coupon.code}
                    onChange={(e) => {
                      handleCouponSelect(coupon, e.target.checked);
                    }}
                    color="primary"
                  />
                }
                label={coupon.friendlyName}
                sx={{ alignSelf: 'flex-start' }}
              />
              {selectedCoupon?.code === coupon.code && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 4 }}>
                  {selectedCoupon.needProof && (
                    <>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id={`coupon-proof-upload-${coupon.code}`}
                        type="file"
                        onChange={handleCouponProofChange}
                      />
                      <label htmlFor={`coupon-proof-upload-${coupon.code}`}>
                        <Button
                          variant="outlined"
                          component="span"
                          size="small"
                          sx={{ direction: 'rtl' }}
                        >
                          העלה אישור
                        </Button>
                      </label>
                      {couponProof && (
                        <Typography variant="body2" color="success.main">
                          ✓ הקובץ נבחר: {couponProof.name}
                        </Typography>
                      )}
                    </>
                  )}
                  {showCouponCode && selectedCoupon && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        קוד קופון שלך:
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: 'primary.main',
                          fontFamily: 'monospace',
                          letterSpacing: 2
                        }}
                      >
                        {selectedCoupon.code}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

export default CouponSelection;